// ProgramProjectFilter.tsx
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, X } from "lucide-react";
import * as React from "react";

/* ============================================================
   Types
============================================================ */
type Currency = {
  id: number;
  symbol: string;
  name: string;
  symbolNative: string;
  decimalDigits: number;
  rounding: number;
  code: string;
  namePlural: string;
  rate: number;
  active: boolean;
};

type Country = { id: number; name: string; currency: Currency };

type Program = { id: number; name: string };

type Project = {
  id: number;
  name: string;
  program: Program;
  country: Country;
};

type Option = { id: number; name: string };

/* ============================================================
   Helpers
============================================================ */
function uniqueById<T extends { id: number }>(arr: T[]): T[] {
  const seen = new Set<number>();
  return arr.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
}

function buildProgramList(projects: Project[]): Option[] {
  return uniqueById(
    projects.map((p) => ({ id: p.program.id, name: p.program.name }))
  ).sort((a, b) => a.name.localeCompare(b.name));
}

function buildProjectList(projects: Project[]): Option[] {
  return uniqueById(projects.map((p) => ({ id: p.id, name: p.name }))).sort(
    (a, b) => a.name.localeCompare(b.name)
  );
}

/* ============================================================
   MultiSelect Component (recherchable + multi)
============================================================ */
function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Sélectionner…",
  emptyText = "Aucun résultat",
  labelRenderer,
  disabled = false,
}: {
  options: Option[];
  value: number[];
  onChange: (next: number[]) => void;
  placeholder?: string;
  emptyText?: string;
  labelRenderer?: (count: number) => string;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const selectedSet = React.useMemo(() => new Set(value), [value]);

  const [search, setSearch] = React.useState("");

  const filteredOptions = React.useMemo(() => {
    if (!search.trim()) return options;
    return options.filter((opt) =>
      opt.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const toggle = (id: number) => {
    const next = new Set(selectedSet);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(Array.from(next));
  };

  const clearOne = (id: number) => {
    onChange(value.filter((v) => v !== id));
  };

  const clearAll = () => onChange([]);

  const selectedOptions =
    value.length > 0 ? options.filter((o) => selectedSet.has(o.id)) : [];

  const buttonLabel =
    labelRenderer?.(value.length) ??
    (value.length > 0 ? `${value.length} sélectionné(s)` : placeholder);

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <span
              className={cn("truncate", value.length === 0 && "opacity-60")}
            >
              {buttonLabel}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[min(640px,90vw)] p-0 z-50">
          <Command shouldFilter={false}>
            <div className="flex items-center gap-2 p-2 border-b">
              <CommandInput
                placeholder="Rechercher…"
                value={search}
                onValueChange={setSearch}
                className="flex-1"
              />
              {value.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={clearAll}
                  className="h-8"
                >
                  Tout effacer
                </Button>
              )}
            </div>

            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup className="max-h-72 overflow-auto">
                {filteredOptions.map((opt) => {
                  const isSelected = selectedSet.has(opt.id);
                  return (
                    <CommandItem
                      key={opt.id}
                      value={opt.name}
                      onSelect={() => toggle(opt.id)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="truncate">{opt.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedOptions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedOptions.map((opt) => (
            <span
              key={opt.id}
              className="inline-flex items-center gap-1 rounded-xl bg-muted px-2 py-1 text-xs"
            >
              {opt.name}
              <button
                type="button"
                className="opacity-70 hover:opacity-100"
                onClick={() => clearOne(opt.id)}
                aria-label={`Supprimer ${opt.name}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Main Component: ProgramProjectFilter
============================================================ */
export default function ProgramProjectFilter({
  projects,
  onFilteredChange,
}: {
  projects: Project[];
  onFilteredChange?: (filtered: Project[]) => void;
}) {
  const [selectedProgramIds, setSelectedProgramIds] = React.useState<number[]>(
    []
  );
  const [selectedProjectIds, setSelectedProjectIds] = React.useState<number[]>(
    []
  );

  const programList = React.useMemo(
    () => buildProgramList(projects),
    [projects]
  );

  const projectList = React.useMemo(() => {
    const base =
      selectedProgramIds.length === 0
        ? projects
        : projects.filter((p) => selectedProgramIds.includes(p.program.id));
    return buildProjectList(base);
  }, [projects, selectedProgramIds]);

  const filteredProjects: Project[] = React.useMemo(() => {
    let out = projects;
    if (selectedProgramIds.length > 0) {
      out = out.filter((p) => selectedProgramIds.includes(p.program.id));
    }
    if (selectedProjectIds.length > 0) {
      out = out.filter((p) => selectedProjectIds.includes(p.id));
    }
    return out;
  }, [projects, selectedProgramIds, selectedProjectIds]);

  React.useEffect(() => {
    onFilteredChange?.(filteredProjects);
  }, [filteredProjects, onFilteredChange]);

  // Purge des projets non compatibles après changement de programme
  React.useEffect(() => {
    if (selectedProjectIds.length === 0) return;
    const allowed = new Set(projectList.map((o) => o.id));
    const next = selectedProjectIds.filter((id) => allowed.has(id));
    if (next.length !== selectedProjectIds.length) {
      setSelectedProjectIds(next);
    }
  }, [projectList, selectedProjectIds]);

  return (
    <div className="grid gap-4 md:grid-cols-2 p-5 bg-[#F7F9FB]">
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Programme(s)</label>
        <MultiSelect
          options={programList}
          value={selectedProgramIds}
          onChange={setSelectedProgramIds}
          placeholder="Choisir des programmes"
          emptyText="Aucun programme"
          labelRenderer={(count) =>
            count > 0 ? `${count} programme(s)` : "Choisir des programmes"
          }
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium">Projet(s)</label>
        <MultiSelect
          options={projectList}
          value={selectedProjectIds}
          onChange={setSelectedProjectIds}
          placeholder={
            selectedProgramIds.length
              ? "Projets du/des programme(s)"
              : "Tous les projets"
          }
          emptyText="Aucun projet"
          labelRenderer={(count) =>
            count > 0 ? `${count} projet(s)` : "Choisir des projets"
          }
        />
      </div>
    </div>
  );
}
